import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  COMPETITION_CATEGORY_LABELS,
  COMPETITION_STATUS_LABELS,
  COMPETITION_STATUS_TONE,
  DISCIPLINE_CODE_LABELS,
  drawKindOf,
  useCompetitionQuery,
} from '@/features/competition';
import { Alert, Badge, Skeleton, Tabs, type TabItem } from '@/shared/ui';

/** Zakładki centrum turnieju — wartość odpowiada segmentowi adresu. */
type TournamentTab =
  | 'informacje'
  | 'zawodnicy'
  | 'losowanie'
  | 'drabinka'
  | 'faza-finalowa'
  | 'mecze';

/**
 * Wspólna rama widoków jednego turnieju: nagłówek z nazwą i statusem oraz
 * zakładki nawigujące po etapach (informacje → zawodnicy → losowanie →
 * drabinka/grupy/tabela → mecze). Zakładka wyników zmienia etykietę wg
 * systemu rozgrywek; system grupy+faza finałowa dostaje dwie osobne
 * zakładki (Faza grupowa i Faza finałowa).
 */
export function TournamentLayout() {
  const { t } = useTranslation('tournament');
  const { competitionId = '' } = useParams<{ competitionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const competitionQuery = useCompetitionQuery(competitionId);
  const competition = competitionQuery.data;

  const drawKind = competition ? drawKindOf(competition.system) : 'none';
  const drawn =
    competition !== undefined &&
    (competition.status === 'IN_PROGRESS' || competition.status === 'COMPLETED');

  // Aktywna zakładka z adresu: /turnieje/:id/<zakładka>[/...]
  const activeTab = (location.pathname.split('/')[3] ?? 'informacje') as TournamentTab;

  const tabs: TabItem<TournamentTab>[] = [
    { value: 'informacje', label: t('center.tabs.info') },
    { value: 'zawodnicy', label: t('center.tabs.participants') },
    ...(drawKind !== 'none' ? [{ value: 'losowanie' as const, label: t('center.tabs.draw') }] : []),
    ...(drawKind !== 'none' && drawn
      ? drawKind === 'groups'
        ? // Dwufazowy system: osobne zakładki fazy grupowej i finałowej.
          [
            { value: 'drabinka' as const, label: t('center.tabs.groupPhase') },
            { value: 'faza-finalowa' as const, label: t('center.tabs.finalPhase') },
          ]
        : [
            {
              value: 'drabinka' as const,
              label: drawKind === 'bracket' ? t('center.tabs.bracket') : t('center.tabs.table'),
            },
          ]
      : []),
    ...(drawn ? [{ value: 'mecze' as const, label: t('center.tabs.matches') }] : []),
  ];

  if (competitionQuery.isError) {
    return (
      <div className="flex flex-col gap-5">
        <Alert tone="danger">{t('center.loadError')}</Alert>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <Link
        to="/turnieje"
        className="text-fg-muted hover:text-fg-strong -mb-1 inline-flex w-fit items-center gap-2 text-sm transition-colors"
      >
        <ArrowLeft className="size-4" />
        {t('center.backToList')}
      </Link>

      {/* Nagłówek turnieju */}
      {competition ? (
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-brand-text mb-1 text-xs font-bold tracking-[0.08em] uppercase">
              {[
                DISCIPLINE_CODE_LABELS[competition.discipline] ?? competition.discipline,
                competition.category
                  ? (COMPETITION_CATEGORY_LABELS[competition.category] ?? competition.category)
                  : null,
              ]
                .filter(Boolean)
                .join(' • ')}
            </p>
            <h1 className="text-fg-strong truncate text-3xl font-extrabold tracking-[-0.03em]">
              {competition.name}
            </h1>
          </div>
          <Badge tone={COMPETITION_STATUS_TONE[competition.status]}>
            {COMPETITION_STATUS_LABELS[competition.status] ?? competition.status}
          </Badge>
        </header>
      ) : (
        <Skeleton className="h-12 w-80" />
      )}

      <Tabs
        label={t('center.tabsLabel')}
        variant="pill"
        items={tabs}
        value={activeTab}
        onValueChange={(tab) => void navigate(`/turnieje/${competitionId}/${tab}`)}
      />

      <Outlet />
    </div>
  );
}
